import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import type {
  ClinicianTrialEndedAnalyticsContext,
  ClinicianTrialEndedModalAction,
  ClinicianTrialEndTargetSeat,
} from '@heidi/commerce/contracts';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';

const TRACK_OPTIONS = { amplitude: false } as const;

const ENDED_MODAL_ACTIONS = {
  upgrade: ButtonAction.Heidi2ClinicianTrialEndedModalUpgrade,
  use_free: ButtonAction.Heidi2ClinicianTrialEndedModalUseFree,
  close: ButtonAction.Heidi2ClinicianTrialEndedModalClose,
} satisfies Record<ClinicianTrialEndedModalAction, ButtonAction>;

// `modal_viewed` has no seat field, so the seat only goes on clicks.
const targetProps = (targetSeat: ClinicianTrialEndTargetSeat) => ({
  target_plan: SubscriptionTier.CLINICIAN_PRO,
  target_seat_type: targetSeat === 'max' ? BillingSeatType.MAX : BillingSeatType.STANDARD,
});

/** After the trial the member is on Free; the recommendation is a Clinician seat. */
export function trackClinicianTrialEndedModalViewed({
  usagePercent,
}: ClinicianTrialEndedAnalyticsContext): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2ClinicianTrialEndedModal,
      current_plan: SubscriptionTier.FREE,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      ...(usagePercent != null && { usage_percent: usagePercent }),
    },
  });
}

export function trackClinicianTrialEndedModalClick({
  action,
  label,
  targetSeat,
  usagePercent,
}: ClinicianTrialEndedAnalyticsContext & {
  action: ClinicianTrialEndedModalAction;
  label: string;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2ClinicianTrialEndedModal,
      action: ENDED_MODAL_ACTIONS[action],
      label,
      current_plan: SubscriptionTier.FREE,
      ...targetProps(targetSeat),
      ...(usagePercent != null && { usage_percent: usagePercent }),
    },
  });
}
